import { ComponentProps, useEffect, useState } from 'react';
import { Image, Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import colors from '../theme';
import { AudiobookCategory, AudiobookTopic, getCategories, getTopics } from '../api/audiobooks';
import { RootStackParamList } from '../navigation/RootNavigator';
import { htmlToPlainText } from '../utils/html';
import { isLoggedIn, logout } from '../api/auth';
import { ChildProfile, getActiveProfile, getCachedActiveProfile } from '../api/profiles';
import ChildProfileBadge from './ChildProfileBadge';

interface BottomTabBarProps {
  active: string;
  onHome?: () => void;
  onDiscover?: () => void;
  onLibrary?: () => void;
  onProfile?: () => void;
}

type IoniconName = ComponentProps<typeof Ionicons>['name'];
type Navigation = NativeStackNavigationProp<RootStackParamList>;
type MenuData = {
  topics: AudiobookTopic[];
  categories: AudiobookCategory[];
  loaded: boolean;
};

const homeIcon = require('../../assets/icon-home.png');

const menuCache: MenuData = {
  topics: [],
  categories: [],
  loaded: false,
};

let menuCachePromise: Promise<MenuData> | null = null;

const tabs: Array<{ label: string; displayLabel: string; icon?: IoniconName; activeIcon?: IoniconName; isHome?: boolean }> = [
  { label: 'My Library', displayLabel: 'Thư viện', icon: 'library-outline', activeIcon: 'library' },
  { label: 'Search', displayLabel: 'Tìm kiếm', icon: 'search-outline', activeIcon: 'search' },
  { label: 'Home', displayLabel: 'Trang chủ', isHome: true },
  { label: 'Settings', displayLabel: 'Cài đặt', icon: 'settings-outline', activeIcon: 'settings' },
  { label: 'Menu', displayLabel: 'Mở rộng', icon: 'menu-outline', activeIcon: 'menu' },
];

function loadMenuData() {
  if (menuCache.loaded) {
    return Promise.resolve(menuCache);
  }

  if (!menuCachePromise) {
    menuCachePromise = Promise.all([getTopics(), getCategories()])
      .then(([topics, categories]) => {
        menuCache.topics = topics;
        menuCache.categories = categories;
        menuCache.loaded = true;
        return menuCache;
      })
      .catch(() => {
        menuCache.loaded = true;
        return menuCache;
      });
  }

  return menuCachePromise;
}

export default function BottomTabBar({ active, onHome, onDiscover, onLibrary, onProfile }: BottomTabBarProps) {
  const navigation = useNavigation<Navigation>();
  const [menuVisible, setMenuVisible] = useState(false);
  const [topics, setTopics] = useState<AudiobookTopic[]>(menuCache.topics);
  const [categories, setCategories] = useState<AudiobookCategory[]>(menuCache.categories);
  const [loaded, setLoaded] = useState(menuCache.loaded);
  const [loggedIn, setLoggedIn] = useState(false);
  const [profile, setProfile] = useState<ChildProfile | null>(() => getCachedActiveProfile());

  useEffect(() => {
    let mounted = true;

    const hasSession = isLoggedIn();
    setLoggedIn(hasSession);
    if (hasSession) {
      const cached = getCachedActiveProfile();
      if (cached) setProfile(cached);

      getActiveProfile()
        .then((item) => {
          if (mounted) setProfile(item);
        })
        .catch(() => {
          if (mounted) setProfile(null);
        });
    } else {
      setProfile(null);
    }

    loadMenuData().then((data) => {
      if (mounted) {
        setTopics(data.topics);
        setCategories(data.categories);
        setLoaded(data.loaded);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    closeMenu();
    setLoggedIn(false);
    setProfile(null);
    navigation.reset({ index: 0, routes: [{ name: 'Login' }] });
  };

  const closeMenu = () => setMenuVisible(false);
  const openList = (title: string, params?: { topic_id?: number; category_id?: number; person_id?: number }) => {
    closeMenu();
    navigation.navigate('Downloads', { title, ...params });
  };

  useEffect(() => {
    if (!menuVisible) return;

    const hasSession = isLoggedIn();
    setLoggedIn(hasSession);
    if (!hasSession) {
      setProfile(null);
      return;
    }

    const cached = getCachedActiveProfile();
    if (cached) setProfile(cached);

    getActiveProfile().then(setProfile).catch(() => setProfile(null));
  }, [menuVisible]);

  const callbacks: Record<string, (() => void) | undefined> = {
    Home: onHome,
    Search: onDiscover,
    'My Library': onLibrary,
    Settings: onProfile,
    Menu: () => setMenuVisible(true),
  };

  return (
    <>
      <View style={styles.container}>
        {tabs.map((tab) => {
          const isActive = active === tab.label;

          return (
            <Pressable key={tab.label} style={[styles.item, tab.isHome && styles.homeItem]} onPress={callbacks[tab.label]}>
              {tab.isHome ? (
                <View style={styles.homeButton}>
                  <Image source={homeIcon} style={styles.homeIcon} resizeMode="contain" />
                </View>
              ) : (
                <>
                  <Ionicons name={isActive ? tab.activeIcon! : tab.icon!} size={24} color={isActive ? colors.button : colors.textSecondary} />
                  <Text style={[styles.label, isActive && styles.active]}>{tab.displayLabel}</Text>
                </>
              )}
            </Pressable>
          );
        })}
      </View>

      <Modal visible={menuVisible} transparent animationType="fade" onRequestClose={closeMenu}>
        <Pressable style={styles.menuBackdrop} onPress={closeMenu}>
          <Pressable style={styles.menuPanel} onPress={(event) => event.stopPropagation()}>
            <View style={styles.menuHeader}>
              <Text style={styles.menuTitle}>Danh mục</Text>
              <Pressable onPress={closeMenu} hitSlop={10}>
                <Ionicons name="close" size={22} color={colors.textPrimary} />
              </Pressable>
            </View>

            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.menuContent}>
              {loggedIn ? (
                <View style={styles.menuProfileWrap}>
                  <ChildProfileBadge profile={profile} subtitle="Đang nghe" onLogout={handleLogout} compact />
                </View>
              ) : (
                <Pressable style={styles.menuItem} onPress={() => { closeMenu(); navigation.navigate('Login'); }}>
                  <View>
                    <Text style={styles.menuItemTitle}>Đăng nhập</Text>
                    <Text style={styles.menuItemSubtitle}>Quản lý tài khoản và thư viện</Text>
                  </View>
                  <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
                </Pressable>
              )}

              <Text style={styles.menuSectionTitle}>Chủ đề</Text>
              {!loaded && <Text style={styles.loadingText}>Đang tải dữ liệu...</Text>}
              {topics.map((topic) => (
                <Pressable key={`topic-${topic.id}`} style={styles.menuItem} onPress={() => openList(topic.name, { topic_id: topic.id })}>
                  <View>
                    <Text style={styles.menuItemTitle}>{topic.name}</Text>
                    {topic.description ? <Text style={styles.menuItemSubtitle} numberOfLines={1}>{htmlToPlainText(topic.description)}</Text> : null}
                  </View>
                  <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
                </Pressable>
              ))}

              <Text style={styles.menuSectionTitle}>Danh mục</Text>
              {categories.map((category) => (
                <Pressable key={`category-${category.id}`} style={styles.menuItem} onPress={() => openList(category.name, { category_id: category.id })}>
                  <View>
                    <Text style={styles.menuItemTitle}>{category.name}</Text>
                    {category.description ? <Text style={styles.menuItemSubtitle} numberOfLines={1}>{htmlToPlainText(category.description)}</Text> : null}
                  </View>
                  <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
                </Pressable>
              ))}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 18,
    height: 88,
    backgroundColor: colors.panel,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    shadowColor: '#0069FF',
    shadowOpacity: 0.16,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: -6 },
    elevation: 12,
  },
  item: {
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 56,
  },
  homeItem: {
    marginTop: -24,
  },
  homeButton: {
    width: 92,
    height: 92,
    borderRadius: 46,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    shadowColor: '#0069FF',
    shadowOpacity: 0.22,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  homeIcon: {
    width: 76,
    height: 76,
  },
  label: {
    color: colors.textSecondary,
    fontSize: 10,
    marginTop: 3,
  },
  active: {
    color: colors.button,
    fontWeight: '900',
  },
  menuBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(33, 33, 37, 0.18)',
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    paddingBottom: 96,
  },
  menuPanel: {
    maxHeight: '72%',
    borderRadius: 16,
    backgroundColor: colors.background,
    padding: 14,
    shadowColor: '#363B64',
    shadowOpacity: 0.16,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
  menuHeader: {
    height: 36,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  menuTitle: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: '700',
  },
  menuContent: {
    paddingBottom: 8,
  },
  menuProfileWrap: {
    marginBottom: 10,
  },
  menuSectionTitle: {
    color: colors.button,
    fontSize: 12,
    fontWeight: '700',
    marginTop: 14,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  menuItem: {
    minHeight: 50,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  menuItemTitle: {
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: '600',
  },
  menuItemSubtitle: {
    color: colors.textSecondary,
    fontSize: 12,
    marginTop: 4,
    maxWidth: 260,
  },
  loadingText: {
    color: colors.textSecondary,
    fontSize: 13,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
});
